import type { CustomerQuoteAllocationBreakdown, CustomerQuoteLine } from "../types";
import { formatCustomerQuoteMoney, normalizeCustomerQuoteMoney } from "./draftService";

/** 使用分为单位的整数，避免分类汇总和周期除法丢失金额精度。 */
function cents(value: string): bigint {
  const normalized = normalizeCustomerQuoteMoney(value);
  if (normalized === null) throw new Error("对客明细金额无效");
  return BigInt(normalized.replace(".", ""));
}

/** 将整数分还原为金额字符串。 */
function money(value: bigint): string {
  const absolute = value < 0n ? -value : value;
  return `${value < 0n ? "-" : ""}${absolute / 100n}.${String(absolute % 100n).padStart(2, "0")}`;
}

/** 用完整角色人均解释总额，周期单价只用于展示，不参与反算。 */
export function describeCustomerQuoteFeeTotal(
  item: CustomerQuoteAllocationBreakdown,
  people: { studentCount: number; teacherCount: number },
): string {
  const studentPrice = cents(item.studentUnitPrice), teacherPrice = cents(item.teacherUnitPrice);
  const studentAmount = studentPrice * BigInt(people.studentCount);
  const teacherAmount = teacherPrice * BigInt(people.teacherCount);
  const total = cents(item.roundedAmount);
  const amountText = (value: bigint) => formatCustomerQuoteMoney(money(value), true);
  const hasStudent = studentAmount !== 0n, hasTeacher = teacherAmount !== 0n;
  if (!hasStudent && !hasTeacher) return `整项计费：${amountText(total)}`;
  const parts: string[] = [];
  if (hasStudent && hasTeacher && studentPrice === teacherPrice) {
    parts.push(`${amountText(studentPrice)} × ${people.studentCount + people.teacherCount}人`);
  } else {
    if (hasStudent) parts.push(`${hasTeacher ? "学生 " : ""}${amountText(studentPrice)} × ${people.studentCount}人`);
    if (hasTeacher) parts.push(`老师 ${amountText(teacherPrice)} × ${people.teacherCount}人`);
  }
  let formula = parts.join(" + ");
  const remainder = total - studentAmount - teacherAmount;
  if (remainder !== 0n) formula += ` ${remainder > 0n ? "+" : "−"} 未分摊金额 ${amountText(remainder < 0n ? -remainder : remainder)}`;
  return `${formula} = ${amountText(total)}`;
}

/** 只使用来源明确且一致的周期；不从人数或名称猜测晚数、天数。 */
function previewRow(item: CustomerQuoteAllocationBreakdown) {
  const sources = item.sourceLines ?? [];
  const field = item.feeCategory === "stay" ? "nightCount"
    : ["bus", "guide"].includes(item.feeCategory) ? "dayCount" : null;
  const count = field ? sources[0]?.[field] : null;
  const hasPeriod = field !== null && count != null && Number.isSafeInteger(count) && count > 0
    && sources.every((source) => source[field] === count);
  const periodCount = hasPeriod ? count : 1;
  const period = hasPeriod ? field === "nightCount" ? "晚" : "天" : "全程";
  const unitCents = cents(item.studentUnitPrice);
  const divisor = BigInt(periodCount);
  const absolute = unitCents < 0n ? -unitCents : unitCents;
  const rounded = (absolute * 2n + divisor) / (divisor * 2n);
  return { ...item, periodCount, period, periodPrice: money(unitCents < 0n ? -rounded : rounded) };
}

type PointSource = Pick<CustomerQuoteLine, "customerQuoteLineId" | "feeCategory" | "sourceSpuId" | "sourceSpuName" | "sourceSpuTableName" | "customerName" | "displayMode">;
type PreviewRow = ReturnType<typeof previewRow> & { pointLabel: string; pointKey: string; pointRowSpan: number; amountRowSpan: number };

/** 按 UUID 关联允许展示的考察点或住宿区域；只补充名称，不重算权威试算金额。 */
export function buildCustomerQuoteFeePreviewGroups(items: CustomerQuoteAllocationBreakdown[], lines: PointSource[] = []) {
  const byId = new Map(lines.filter((line) => line.customerQuoteLineId).map((line) => [line.customerQuoteLineId, line]));
  const groups = new Map<string, { category: string; total: string; hasPoints: boolean; rows: PreviewRow[] }>();
  for (const item of items) {
    const hasPoints = ["stay", "ticket", "enterprise", "intangible"].includes(item.feeCategory);
    const group = groups.get(item.feeCategory) ?? { category: item.feeCategory, total: "0.00", hasPoints, rows: [] };
    group.total = money(cents(group.total) + cents(item.roundedAmount));
    const names: { pointLabel: string; pointKey: string; customerName: string }[] = [];
    if (hasPoints) {
      for (const source of item.sourceLines ?? []) {
        const line = source.lineId ? byId.get(source.lineId) : undefined;
        const visible = line?.displayMode === "show" && line.feeCategory === item.feeCategory;
        const knownLocation = item.feeCategory !== "stay"
          || ["dim_stay_area", "resource_stay_budget_spu"].includes(line?.sourceSpuTableName ?? "");
        const pointLabel = visible && knownLocation ? line.sourceSpuName?.trim() ?? "" : "";
        const pointKey = pointLabel ? line?.sourceSpuId?.trim() ? `id:${line.sourceSpuId.trim()}` : `name:${pointLabel}` : "";
        const customerName = visible ? line.customerName : item.customerName;
        if (!names.some((name) => name.pointKey === pointKey && name.customerName === customerName)) names.push({ pointLabel, pointKey, customerName });
      }
    }
    if (!names.length) names.push({ pointLabel: "", pointKey: "", customerName: item.customerName });
    const row = previewRow(item);
    group.rows.push(...names.map((name, index) => ({ ...row, ...name, pointRowSpan: 1, amountRowSpan: index === 0 ? names.length : 0 })));
    groups.set(item.feeCategory, group);
  }
  for (const group of groups.values()) {
    let first: PreviewRow | undefined;
    for (const row of group.rows) {
      if (first && row.pointKey && first.pointKey === row.pointKey) {
        first.pointRowSpan += 1;
        row.pointRowSpan = 0;
      } else first = row;
    }
  }
  return [...groups.values()];
}
